import type { IContextualMenuItem } from '@fluentui/react';
import { MessageBarType, Stack } from '@fluentui/react';
import {
  Button,
  buttonStylesIcon,
  buttonStylesIconCard,
  IconName,
  Item,
  itemStylesTag,
  ListCell,
  TooltipHost,
  useClassNames,
  useTheme,
  useToast,
} from '@h2oai/ui-kit';
import { useCallback, useMemo } from 'react';

import { roleBindingResourceCompile } from '../../../../authz/authz.functions';
import { useQueryPermissionCurrentUser } from '../../../../authz/authz.hooks';
import { WorkspacesUserActionType } from '../../../../authz/userActions';
import { formatError } from '../../../../utils/utils';
import { useWorkspaces } from '../../../Orchestrator/WorkspaceProvider';
import { usersCircleListStyle } from './parts.styles';
import {
  UserCircleProps,
  UsersCircleListClassNames,
  UsersCircleListStyles,
  WorkspaceItemActionsProps,
  WorkspaceSharedWithCellPureProps,
} from './types';

export const getInitials = (fullName?: string) => {
  if (!fullName) return '';

  const trimmedName = fullName.trim();
  const names = trimmedName.split(' ');

  const firstName = names[0];
  const lastName = names[names.length - 1];

  if (names.length === 1) {
    return `${firstName.charAt(0).toUpperCase()}`;
  }

  return `${firstName.charAt(0).toUpperCase()}${lastName.charAt(0).toUpperCase()}`;
};

export function getFirstItemsAndCount<T>(array: T[]) {
  const items = array.slice(0, 5);
  const countLeft = array.length - items.length;

  return { items, countLeft };
}

export const UsersCircleList = (props: UserCircleProps) => {
  const { items, countLeft } = useMemo(() => {
    return getFirstItemsAndCount(props.usersDisplayNames || []);
  }, [props.usersDisplayNames]);
  const classNames = useClassNames<UsersCircleListStyles, UsersCircleListClassNames>(
    'UsersCircleList',
    usersCircleListStyle
  );

  return (
    <Stack horizontal className={classNames.root} verticalAlign="center">
      {items?.map((userDisplayName, idx) => (
        <div className={classNames.circle} key={idx}>
          <span>{getInitials(userDisplayName)}</span>
        </div>
      ))}
      {countLeft > 0 && <div className={classNames.usersNumber}>+{countLeft}</div>}
    </Stack>
  );
};

export const WorkspaceSharedWithCellPure = (props: WorkspaceSharedWithCellPureProps) => {
  const { usersDisplayNames, groupsDisplayNames } = props;

  const uniqueUsersDisplayNames = [...new Set(usersDisplayNames)];
  const uniqueGroupsDisplayNames = [...new Set(groupsDisplayNames)];

  const tooltipContent = groupsDisplayNames?.length ? (
    <Stack>
      <Stack>Users: {uniqueUsersDisplayNames.join(', ')}</Stack>
      <Stack>Groups: {uniqueGroupsDisplayNames.join(', ')}</Stack>
    </Stack>
  ) : (
    uniqueUsersDisplayNames.join(', ')
  );
  return (
    <ListCell
      headerText={props.header}
      text={''}
      onRenderText={() => (
        <TooltipHost id="users-tooltip" content={tooltipContent}>
          <UsersCircleList usersDisplayNames={uniqueUsersDisplayNames} />
        </TooltipHost>
      )}
    />
  );
};

export const WorkspaceItemActions = (props: WorkspaceItemActionsProps) => {
  const { palette } = useTheme();
  const { workspace, setWorkspaceForEdit, setWorkspaceForView } = props;
  const isDeleted = Boolean(workspace.deleteTime || workspace.deleter);
  const isArchived = Boolean(workspace.archiveTime || workspace.archiver);
  const canViewQuery = useQueryPermissionCurrentUser({
    action: WorkspacesUserActionType.WORKSPACE_GET,
    resource: roleBindingResourceCompile(workspace.name!),
  });
  const canView = Boolean(canViewQuery.data?.allowed);
  const canEditQuery = useQueryPermissionCurrentUser({
    action: WorkspacesUserActionType.WORKSPACE_EDIT,
    resource: roleBindingResourceCompile(workspace.name!),
  });
  const canEdit = Boolean(canEditQuery.data?.allowed);
  const canDeleteQuery = useQueryPermissionCurrentUser({
    action: WorkspacesUserActionType.WORKSPACE_DELETE,
    resource: roleBindingResourceCompile(workspace.name!),
  });
  const canArchiveQuery = useQueryPermissionCurrentUser({
    action: WorkspacesUserActionType.WORKSPACE_ARCHIVE,
    resource: roleBindingResourceCompile(workspace.name!),
  });
  const canDelete = Boolean(canDeleteQuery.data?.allowed);
  const canArchive = Boolean(canArchiveQuery.data?.allowed);
  const {
    ACTIVE_WORKSPACE_NAME,
    defaultWorkspace,
    activateWorkspace,
    deleteWorkspace,
    undeleteWorkspace,
    archiveWorkspace,
    unarchiveWorkspace,
  } = useWorkspaces();
  const isActiveWorkspace = ACTIVE_WORKSPACE_NAME === workspace.name;
  const deleteDisabled = !canDelete || isActiveWorkspace;
  const archiveDisabled = !canArchive || isActiveWorkspace;
  const { addToast, removeAllToasts } = useToast();
  const defaultWorkspaceName = defaultWorkspace?.name;

  const switchToDefaultWorkspaceIfActive = useCallback(async () => {
    if (isActiveWorkspace && defaultWorkspaceName) activateWorkspace(defaultWorkspaceName);
  }, [isActiveWorkspace, defaultWorkspaceName, activateWorkspace]);

  type WorkspaceActionConfig = {
    action: () => Promise<unknown>;
    failureMessage: string;
    clearToasts?: boolean;
    ensureDefaultActive?: boolean;
  };

  const runWorkspaceAction = useCallback(
    async (config: WorkspaceActionConfig) => {
      const { action, failureMessage, clearToasts = false, ensureDefaultActive = false } = config;
      try {
        if (ensureDefaultActive) await switchToDefaultWorkspaceIfActive();

        await action();

        if (clearToasts) removeAllToasts();
      } catch (error) {
        const message = `${failureMessage}: ${formatError(error)}`;
        console.error(message);
        addToast({ messageBarType: MessageBarType.error, message });
      }
    },
    [switchToDefaultWorkspaceIfActive, removeAllToasts, addToast, formatError]
  );

  const handleUndeleteWorkspace = useCallback(
    async (workspaceName: string) => {
      await runWorkspaceAction({
        action: () => undeleteWorkspace(workspaceName),
        failureMessage: 'Failed to undelete workspace',
        clearToasts: true,
      });
    },
    [runWorkspaceAction, undeleteWorkspace]
  );

  const handleUnarchiveWorkspace = useCallback(
    async (workspaceName: string) => {
      await runWorkspaceAction({
        action: () => unarchiveWorkspace(workspaceName),
        failureMessage: 'Failed to unarchive workspace',
        clearToasts: true,
      });
    },
    [runWorkspaceAction, unarchiveWorkspace]
  );

  const handleDeleteWorkspace = useCallback(
    async (workspaceName: string) => {
      await runWorkspaceAction({
        action: () => deleteWorkspace(workspaceName),
        failureMessage: 'Failed to delete workspace',
        ensureDefaultActive: true,
      });
    },
    [runWorkspaceAction, deleteWorkspace]
  );

  const handleArchiveWorkspace = useCallback(
    async (workspaceName: string) => {
      await runWorkspaceAction({
        action: () => archiveWorkspace(workspaceName),
        failureMessage: 'Failed to archive workspace',
        ensureDefaultActive: true,
      });
    },
    [runWorkspaceAction, archiveWorkspace]
  );

  const menuItems: IContextualMenuItem[] = useMemo(() => {
    if (isDeleted || isArchived) {
      const restoreWorkspace = isDeleted ? handleUndeleteWorkspace : handleUnarchiveWorkspace;
      return [
        {
          key: 'workspace-restore',
          text: 'Restore',
          onClick: () => {
            restoreWorkspace(String(workspace.name));
          },
          'data-test': 'workspace-undelete-button',
          iconProps: { iconName: 'RevToggleKey' },
        },
      ];
    }

    const items: IContextualMenuItem[] = [];

    if (!isActiveWorkspace) {
      items.push({
        key: 'workspace-set-active',
        text: 'Set active',
        onClick: () => {
          activateWorkspace(workspace.name || '');
        },
        'data-test': 'workspace-set-active',
        iconProps: { iconName: 'Play' },
      });
    }

    items.push({
      key: 'workspace-view',
      text: 'View',
      onClick: canView
        ? () => {
            setWorkspaceForView(String(workspace.name));
          }
        : undefined,
      disabled: !canView,
      'data-test': 'workspace-view-button',
      style: { ...(canView ? {} : { opacity: 0.5 }) },
      iconProps: { iconName: 'Info' },
    });

    items.push({
      key: 'workspace-edit',
      text: 'Edit',
      onClick: canEdit
        ? () => {
            setWorkspaceForEdit(String(workspace.name));
          }
        : undefined,
      disabled: !canEdit,
      'data-test': 'workspace-edit-button',
      style: { ...(canEdit ? {} : { opacity: 0.5 }) },
      iconProps: { iconName: 'Edit' },
    });

    items.push({
      key: 'workspace-delete',
      text: 'Delete',
      onClick: !deleteDisabled
        ? () => {
            handleDeleteWorkspace(String(workspace.name));
          }
        : undefined,
      disabled: deleteDisabled,
      'data-test': 'workspace-delete-button',
      style: { color: 'var(--h2o-red400)', ...(!deleteDisabled ? {} : { opacity: 0.5 }) },
      iconProps: {
        iconName: 'Delete',
        style: { color: 'var(--h2o-red400)' },
      },
    });

    // TODO: uncomment when archiving is handled in the services
    // items.push({
    //   key: 'workspace-archive',
    //   text: 'Archive',
    //   onClick: !archiveDisabled
    //     ? () => {
    //         handleArchiveWorkspace(String(workspace.name));
    //       }
    //     : undefined,
    //   disabled: archiveDisabled,
    //   'data-test': 'workspace-archive-button',
    //   style: { color: 'var(--h2o-yellow800)', ...(!archiveDisabled ? {} : { opacity: 0.5 }) },
    //   iconProps: {
    //     iconName: 'Archive',
    //     style: { color: 'var(--h2o-yellow800)' },
    //   },
    // });

    return items;
  }, [
    isDeleted,
    isArchived,
    workspace.name,
    handleUndeleteWorkspace,
    handleUnarchiveWorkspace,
    isActiveWorkspace,
    activateWorkspace,
    canView,
    canEdit,
    setWorkspaceForEdit,
    setWorkspaceForView,
    deleteDisabled,
    handleDeleteWorkspace,
    archiveDisabled,
    handleArchiveWorkspace,
  ]);

  return (
    <Stack horizontal tokens={{ childrenGap: 16 }} horizontalAlign="end" verticalAlign="center">
      {workspace.name === ACTIVE_WORKSPACE_NAME && (
        <Item
          data={{
            style: {
              backgroundColor: 'transparent',
              padding: '0px 20px',
              fontSize: 14,
            },
            title: 'Active',
          }}
          labelField="title"
          styleField="style"
          className="workspace-active"
          styles={itemStylesTag}
        />
      )}

      {isDeleted && (
        <Item
          data={{
            style: {
              background: palette?.red500,
              color: palette?.white,
            },
            title: 'Deleted',
          }}
          labelField="title"
          styleField="style"
          styles={itemStylesTag}
        />
      )}

      {isArchived && (
        <Item
          data={{
            style: {
              background: palette?.yellow700,
              color: palette?.white,
            },
            title: 'Archived',
          }}
          labelField="title"
          styleField="style"
          styles={itemStylesTag}
        />
      )}

      <Button
        styles={[
          buttonStylesIcon,
          buttonStylesIconCard,
          { root: { minWidth: '32px', i: { fontSize: '24px', transform: 'rotate(90deg)' } } },
        ]}
        menuIconName={IconName.More}
        menuItems={menuItems}
      />
    </Stack>
  );
};
